import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import LeaveService from '../services/leaveService';

const LeaveRequestsPage = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [rejectionModal, setRejectionModal] = useState({ show: false, leaveId: null, reason: '' });
  const [filters, setFilters] = useState({
    status: 'PENDING',
    startDate: '',
    endDate: '',
    userId: ''
  });

  useEffect(() => {
    fetchLeaves();
  }, [filters]);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await LeaveService.getAllLeaves(filters);
      setLeaves(response.data);
    } catch (error) {
      setError(error.error || 'Failed to fetch leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'PENDING',
      startDate: '',
      endDate: '',
      userId: ''
    });
  };

  const handleApprove = async (leaveId) => {
    if (!window.confirm('Are you sure you want to approve this leave request?')) {
      return;
    }

    try {
      setProcessingId(leaveId);
      await LeaveService.approveLeave(leaveId);
      fetchLeaves();
    } catch (error) {
      alert(error.error || 'Failed to approve leave request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (leaveId) => {
    setRejectionModal({ show: true, leaveId, reason: '' });
  };

  const handleRejectSubmit = async () => {
    try {
      setProcessingId(rejectionModal.leaveId);
      await LeaveService.rejectLeave(rejectionModal.leaveId, rejectionModal.reason);
      setRejectionModal({ show: false, leaveId: null, reason: '' });
      fetchLeaves();
    } catch (error) {
      alert(error.error || 'Failed to reject leave request');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { class: 'status-pending', text: 'Pending' },
      APPROVED: { class: 'status-approved', text: 'Approved' },
      REJECTED: { class: 'status-rejected', text: 'Rejected' }
    };

    const config = statusConfig[status] || { class: 'status-unknown', text: status };
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDuration = (leave) => {
    if (leave.durationType === 'HALF_DAY') {
      return '0.5 day';
    }

    return `${leave.totalDays} day${leave.totalDays > 1 ? 's' : ''}`;
  };

  const getEmployeeDisplayName = (leave) => {
    const userData = leave?.userId || {};
    const fullNameFromParts = [userData.firstName, userData.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    return (
      userData.name ||
      userData.fullName ||
      fullNameFromParts ||
      'Unknown Employee'
    );
  };

  if (!['HRO', 'ADMIN', 'CHAIRMAN'].includes(user?.role)) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="error-message">
            Access denied. Only HRO, Admin, and Chairman can access this page.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="page-header">
          <h1>Leave Requests Management</h1>
          <div className="header-stats">
            <span className="stat-item">
              Total Requests: {leaves.length}
            </span>
            <span className="stat-item">
              Pending: {leaves.filter(l => l.status === 'PENDING').length}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="startDate">From Date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="endDate">To Date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            <button className="btn btn-secondary" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>

        {/* Leave Requests Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-message">Loading leave requests...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : leaves.length === 0 ? (
            <div className="empty-state">
              <p>No leave requests found.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Date(s)</th>
                  <th>Duration</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Applied On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaves.map((leave) => (
                  <tr key={leave._id}>
                    <td>
                      <div>
                        <div className="employee-name">{getEmployeeDisplayName(leave)}</div>
                        {leave?.userId?.employeeId && (
                          <small className="employee-email">ID: {leave.userId.employeeId}</small>
                        )}
                        {leave?.userId?.phoneNumber && (
                          <small className="employee-email">{leave.userId.phoneNumber}</small>
                        )}
                        {leave?.userId?.email && (
                          <small className="employee-email">{leave.userId.email}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="leave-type-info">
                        <div
                          className="leave-type-indicator"
                          style={{ backgroundColor: leave.leaveTypeId?.color || '#6B7280' }}
                        ></div>
                        <div>
                          <div>{leave.leaveTypeId?.name || 'Unknown'}</div>
                          <small className={`duration-badge ${leave.durationType?.toLowerCase() || 'unknown'}`}>
                            {leave.durationType === 'HALF_DAY' ? 'Half Day' :
                              leave.durationType === 'FULL_DAY' ? 'Full Day' : 'Unknown'}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {formatDate(leave.startDate)}
                      {leave.endDate && leave.endDate !== leave.startDate && (
                        <> - {formatDate(leave.endDate)}</>
                      )}
                    </td>
                    <td>{calculateDuration(leave)}</td>
                    <td className="reason-cell" title={leave.reason}>
                      {leave.reason.length > 50
                        ? `${leave.reason.substring(0, 50)}...`
                        : leave.reason
                      }
                    </td>
                    <td>{getStatusBadge(leave.status)}</td>
                    <td>{formatDate(leave.createdAt)}</td>
                    <td>
                      {leave.status === 'PENDING' ? (
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleApprove(leave._id)}
                            disabled={processingId === leave._id}
                          >
                            {processingId === leave._id ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRejectClick(leave._id)}
                            disabled={processingId === leave._id}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <div className="processed-info">
                          <div>
                            {leave.approvedBy?.name ||
                              leave.approvedBy?.fullName ||
                              leave.approvedBy?.username ||
                              leave.approvedBy?.phoneNumber ||
                              'Unknown'}
                          </div>
                          <small>
                            {leave.approvedAt && formatDate(leave.approvedAt)}
                            {leave.rejectedAt && formatDate(leave.rejectedAt)}
                          </small>
                          {leave.rejectionReason && (
                            <div className="rejection-reason" title={leave.rejectionReason}>
                              Reason: {leave.rejectionReason.length > 30
                                ? `${leave.rejectionReason.substring(0, 30)}...`
                                : leave.rejectionReason
                              }
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Rejection Modal */}
        {rejectionModal.show && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Reject Leave Request</h2>
                <button
                  className="modal-close"
                  onClick={() => setRejectionModal({ show: false, leaveId: null, reason: '' })}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="rejectionReason">Reason for Rejection</label>
                  <textarea
                    id="rejectionReason"
                    value={rejectionModal.reason}
                    onChange={(e) => setRejectionModal(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Please provide a reason for rejecting this leave request..."
                    rows="4"
                    maxLength="500"
                  />
                  <small className="char-count">
                    {rejectionModal.reason.length}/500 characters
                  </small>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setRejectionModal({ show: false, leaveId: null, reason: '' })}
                  disabled={processingId}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleRejectSubmit}
                  disabled={processingId || !rejectionModal.reason.trim()}
                >
                  {processingId ? 'Processing...' : 'Reject Request'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LeaveRequestsPage;